import React, { useState } from 'react';
import { CreditCard, FileText, ChevronDown, ChevronUp, Calendar, DollarSign, Tag, X, Loader2 } from 'lucide-react';
import StripePaymentForm from '../StripePaymentForm';
import { validateDiscountCode } from '../../../services/discountCodeService';

const ReviewPayment = ({
  bookingData,
  vendor,
  service,
  calculateSubtotal,
  getSubtotalAfterDiscount,
  calculateCommission,
  calculateTotal,
  calculateDepositAmount,
  calculatePaymentTerms,
  calculateEndTime,
  calculateProportionalPlatformFee,
  commissionSetting,
  appliedDiscount,
  onApplyDiscount,
  onRemoveDiscount,
  onRetry,
  onPaymentSuccess,
  onPaymentError,
  cardholderName,
  onCardholderNameChange
}) => {
  const [showTerms, setShowTerms] = useState(false);
  const [showPaymentTerms, setShowPaymentTerms] = useState(true);
  const [discountInput, setDiscountInput] = useState('');
  const [discountError, setDiscountError] = useState('');
  const [validatingDiscount, setValidatingDiscount] = useState(false);

  const hasTerms = vendor?.terms_conditions && vendor.terms_conditions.trim().length > 0;
  const paymentTerms = calculatePaymentTerms?.();
  const subtotalBeforeDiscount = calculateSubtotal?.() ?? 0;
  const subtotal = getSubtotalAfterDiscount ? getSubtotalAfterDiscount() : subtotalBeforeDiscount;
  const totalCommission = calculateCommission();
  
  // Calculate proportional platform fee for deposit payment
  const depositPercentage = paymentTerms?.depositPercentage || 50;
  const depositPlatformFee = calculateProportionalPlatformFee 
    ? calculateProportionalPlatformFee(depositPercentage)
    : (totalCommission * depositPercentage) / 100;
  return (
    <div>
      <h2 className="text-xl sm:text-2xl font-bold text-black mb-6 sm:mb-8">
        Review & Payment
      </h2>
      <div className="space-y-8">
        <div className="border-2 border-gray-200 rounded-lg p-6">
          <h3 className="font-bold text-lg mb-4 text-black">Booking Summary</h3>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="text-gray-600 font-medium">Package:</span>
              <span className="text-black font-semibold">
                {bookingData.selectedPackage?.name}
              </span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="text-gray-600 font-medium">Date:</span>
              <span className="text-black font-semibold">{bookingData.eventDate}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="text-gray-600 font-medium">Time:</span>
              <span className="text-black font-semibold">{bookingData.eventTime}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="text-gray-600 font-medium">Duration:</span>
              <span className="text-black font-semibold">
                {bookingData.duration} hours
              </span>
            </div>
            <div className="flex justify-between items-start py-2">
              <span className="text-gray-600 font-medium">Location:</span>
              <span className="text-black font-semibold text-right max-w-[60%]">
                {bookingData.location || 'Not specified'}
              </span>
            </div>
          </div>
          <div className="mt-6 pt-6 border-t-2 border-gray-200">
            {vendor?.id && (
              <div className="mb-4 pb-4 border-b border-gray-200">
                {!appliedDiscount ? (
                  <div className="flex flex-col sm:flex-row gap-2">
                    <div className="flex-1 flex gap-2">
                      <input
                        type="text"
                        value={discountInput}
                        onChange={(e) => { setDiscountInput(e.target.value.toUpperCase()); setDiscountError(''); }}
                        placeholder="Discount code"
                        className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                      <button
                        type="button"
                        onClick={async () => {
                          const code = discountInput.trim();
                          if (!code) { setDiscountError('Enter a code'); return; }
                          setValidatingDiscount(true);
                          setDiscountError('');
                          try {
                            const res = await validateDiscountCode({
                              vendor_id: vendor.id,
                              code,
                              subtotal_amount: subtotalBeforeDiscount,
                            });
                            if (res.valid && res.data) {
                              onApplyDiscount?.(res.data);
                              setDiscountInput('');
                            } else {
                              setDiscountError(res.message || 'Invalid or expired code');
                            }
                          } catch (e) {
                            setDiscountError(e?.response?.data?.message || 'Could not validate code');
                          } finally {
                            setValidatingDiscount(false);
                          }
                        }}
                        disabled={validatingDiscount}
                        className="px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-md hover:bg-gray-800 disabled:opacity-50 flex items-center gap-2"
                      >
                        {validatingDiscount ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                        Apply
                      </button>
                    </div>
                    {discountError && <p className="text-sm text-red-600">{discountError}</p>}
                  </div>
                ) : (
                  <div className="flex items-center justify-between py-2 px-3 bg-green-50 border border-green-200 rounded-lg">
                    <span className="text-sm font-medium text-green-800 flex items-center gap-2">
                      <Tag className="w-4 h-4" />
                      {appliedDiscount.code}: -${Number(appliedDiscount.discount_amount).toFixed(2)} applied
                    </span>
                    <button
                      type="button"
                      onClick={() => { onRemoveDiscount?.(); setDiscountError(''); }}
                      className="text-green-700 hover:text-green-900 p-1"
                      title="Remove code"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            )}
            <div className="space-y-2 text-sm">
              <div className="flex justify-between items-center">
                <span className="text-gray-600 font-medium">Subtotal:</span>
                <span className="text-black font-semibold">
                  ${subtotal.toFixed(2)}
                  {appliedDiscount && (
                    <span className="text-gray-500 font-normal ml-1">
                      (after ${Number(appliedDiscount.discount_amount).toFixed(2)} discount)
                    </span>
                  )}
                </span>
              </div>
              {commissionSetting &&
                commissionSetting.is_active &&
                calculateCommission() > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 font-medium">Platform Fee:</span>
                    <span className="text-black font-semibold">
                      ${calculateCommission().toFixed(2)}
                      {commissionSetting.commission_type === 'percentage' && (
                        <span className="text-xs text-gray-500 ml-1">
                          ({commissionSetting.commission_rate}%)
                        </span>
                      )}
                    </span>
                  </div>
                )}
              <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                <span className="text-gray-600 font-medium">
                  Deposit {paymentTerms ? `(${paymentTerms.depositLabel})` : '(50%)'}:
                </span>
                <span className="text-black font-semibold">
                  ${calculateDepositAmount().toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between items-center pt-2 border-t-2 border-gray-300">
                <span className="text-black font-bold">Amount Due Now:</span>
                <span className="text-black font-bold text-lg">
                  ${calculateDepositAmount().toFixed(2)}
                </span>
              </div>
              <div className="text-xs text-gray-500 text-center pt-1">
                Full Total: ${calculateTotal().toFixed(2)}
              </div>
            </div>
          </div>
        </div>

        {/* Payment Terms Section */}
        {paymentTerms && (
          <div className="border-2 border-blue-200 rounded-lg p-6 bg-blue-50">
            <button
              onClick={() => setShowPaymentTerms(!showPaymentTerms)}
              className="w-full flex items-center justify-between text-left mb-4"
            >
              <div className="flex items-center">
                <DollarSign className="w-5 h-5 text-blue-600 mr-3" />
                <h3 className="font-bold text-lg text-black">
                  Payment Terms
                </h3>
              </div>
              {showPaymentTerms ? (
                <ChevronUp className="w-5 h-5 text-gray-600" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-600" />
              )}
            </button>
            {showPaymentTerms && (
              <div className="space-y-4">
                <div className="bg-white rounded-lg p-4 border border-blue-200">
                  <p className="text-sm text-gray-700 mb-4">{paymentTerms.description}</p>
                  
                  <div className="space-y-3">
                    <h4 className="font-semibold text-sm text-black mb-2">Payment Schedule:</h4>
                    {paymentTerms.schedule.map((payment, index) => {
                      const amount = (subtotal * payment.percentage / 100);
                      // Calculate proportional platform fee for this payment
                      const proportionalFee = calculateProportionalPlatformFee 
                        ? calculateProportionalPlatformFee(payment.percentage)
                        : (totalCommission * payment.percentage) / 100;
                      const paymentAmount = amount + proportionalFee;
                      
                      return (
                        <div key={index} className="flex items-start justify-between py-2 border-b border-gray-200 last:border-0">
                          <div className="flex-1">
                            <div className="flex items-center mb-1">
                              <Calendar className="w-4 h-4 text-blue-600 mr-2" />
                              <span className="font-medium text-sm text-black">{payment.label}</span>
                              <span className="ml-2 text-xs text-gray-500">({payment.percentage}%)</span>
                            </div>
                            <p className="text-xs text-gray-600 ml-6">{payment.dueDate}</p>
                          </div>
                          <div className="text-right">
                            <span className="font-bold text-sm text-black">
                              ${paymentAmount.toFixed(2)}
                            </span>
                            {proportionalFee > 0 && (
                              <p className="text-xs text-gray-500 mt-1">
                                (includes ${proportionalFee.toFixed(2)} platform fee)
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                <div className="bg-blue-100 rounded-lg p-4 border border-blue-300">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-sm text-black">Amount Due Now:</span>
                    <span className="font-bold text-lg text-black">
                      ${calculateDepositAmount().toFixed(2)}
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 mt-1">
                    This includes {paymentTerms.depositLabel} of package price + platform fee
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Terms & Conditions Section */}
        {hasTerms && (
          <div className="border-2 border-gray-200 rounded-lg p-6">
            <button
              onClick={() => setShowTerms(!showTerms)}
              className="w-full flex items-center justify-between text-left"
            >
              <div className="flex items-center">
                <FileText className="w-5 h-5 text-black mr-3" />
                <h3 className="font-bold text-lg text-black">
                  Terms & Conditions
                </h3>
              </div>
              {showTerms ? (
                <ChevronUp className="w-5 h-5 text-gray-600" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-600" />
              )}
            </button>
            {showTerms && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="prose prose-sm max-w-none">
                  <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                    {vendor.terms_conditions}
                  </p>
                </div>
                <p className="text-xs text-gray-500 mt-4 italic">
                  By proceeding with payment, you agree to the above terms and conditions.
                </p>
              </div>
            )}
          </div>
        )}

        <div>
          <h3 className="font-bold text-lg mb-4 text-black">Payment Information</h3>
          {bookingData.selectedPackage && vendor && service ? (
            <StripePaymentForm
              amount={calculateDepositAmount()}
              bookingData={{
                vendor_id: vendor.id,
                service_id: service.id,
                package_id: bookingData.selectedPackage.id,
                event_date: bookingData.eventDate,
                start_time: bookingData.eventTime,
                end_time: calculateEndTime(bookingData.eventTime, bookingData.duration),
                duration_hours: parseFloat(bookingData.duration),
                guest_count: parseInt(bookingData.guestCount) || null,
                event_type: bookingData.eventType,
                event_location: bookingData.location,
                event_address: bookingData.location,
                special_requirements: bookingData.specialRequests,
                subtotal_amount: subtotal,
                platform_fee: totalCommission,
                payment_platform_fee: depositPlatformFee,
                payment_percentage: depositPercentage,
                commission_rate: commissionSetting?.commission_rate || null,
                commission_type: commissionSetting?.commission_type || null,
                commission_setting_id: commissionSetting?.id || null,
                discount_code_id: appliedDiscount?.discount_code_id || null,
                discount_amount: appliedDiscount?.discount_amount ?? 0,
                total_amount: calculateTotal(),
                deposit_amount: calculateDepositAmount(),
              }}
              paymentType={depositPercentage === 100 ? 'full_payment' : 'deposit'}
              onSuccess={onPaymentSuccess}
              onError={onPaymentError}
              metadata={{
                cardholderName: cardholderName,
                onCardholderNameChange: onCardholderNameChange
              }}
            />
          ) : (
            <div className="border-2 border-gray-200 rounded-lg p-6">
              <div className="flex items-center justify-center py-8">
                <div className="text-center">
                  <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                    <CreditCard className="w-6 h-6 text-gray-400" />
                  </div>
                  <p className="text-gray-600 mb-4">Missing booking information</p>
                  <button
                    onClick={onRetry}
                    className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-900 transition-colors"
                  >
                    Go Back
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReviewPayment;

